import { isMobile, isTablet } from "react-device-detect";
import { IPropsBigCard } from "../../../types";
import * as Styled from "./BigCard.styled";
import { StarRating } from "../../star-rating";
import { Clock, StarFill, PlayFill } from 'react-bootstrap-icons';
import { durationFormatter, truncateWords } from "../../../libs/utils";
import { useEffect, useRef } from "react";


export const BigCard: React.FC<IPropsBigCard> = ({
    mediaItem,
    mediaRating,
    enableRatingControls,
    onWatchClick,
    onRatingSuccess,
    isSignedIn,
    hasEligiblePlan,
    sessionToken,
    customHeight,
    customBorderRadius,
    onlyShowWatchButton = false,
    useNewDesign,
    isClickable = false,
    disableOverlay = false,
    alignInfoRowStart = false,
    alignInfoRowSmallCenter = false,
    limitDescriptionLines = false,
    enableCompetitionMode,
    showSeparateMetaLayout
}) => {
    const infoRowRef = useRef<HTMLDivElement>(null);
    const LABEL_WATCH_NOW = 'Watch Now';
    const LABEL_GET_ACCESS = 'Get Access';
    const LABEL_PURCHASE_PLAN = "Purchase Plan";

    /**
     * @function mobileClassCheck
     * @description Check if device is mobile or tablet and return className.
     * @returns {String}
     */
    function mobileClassCheck() {
        return (isMobile || isTablet) ? "is-mobile" : "is-not-mobile";
    }

    useEffect(() => {
        const el = infoRowRef.current;
        if (!el) return;

        const handleWheel = (e: WheelEvent) => {
            // Scroll horizontally instead of vertically
            if (e.deltaY === 0) return;
            e.preventDefault();
            el.scrollBy({
                left: e.deltaY,
                behavior: 'smooth'
            });
        };

        el.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            el.removeEventListener('wheel', handleWheel);
        };
    }, []);

    function handleWatchClick() {
        onWatchClick(mediaItem);
    }

    const shouldShowRating = !enableCompetitionMode;
    const canRate = enableRatingControls && isSignedIn && hasEligiblePlan;
    const matchedRating = mediaRating?.mediaId === mediaItem?.mediaId ? mediaRating : undefined;


    return (
        <Styled.MediaCard className={`media-card ${mobileClassCheck()}`}>
            <Styled.MediaCardWrapper
                onClick={isClickable ? handleWatchClick : undefined}
                className={`media-card--wrapper ${mobileClassCheck()}`} customHeight={customHeight} customBorderRadius={customBorderRadius} >

                <Styled.MediaPoster className={`media-poster ${mobileClassCheck()}`}>
                    {!disableOverlay && (
                        <Styled.TrailerOverlay>
                            {useNewDesign ? (
                                // Show new design if useNewDesign is true
                                <Styled.NewMetaContent>
                                    <Styled.NewInfoRow>
                                        {shouldShowRating && (
                                            <Styled.NewInfoItem>
                                                <StarFill color="currentColor" size={20} />
                                                {/* <span>11,134 ratings</span> */}
                                                <span>{(matchedRating?.rating ?? 0)} / 5</span>

                                                <Styled.Divider>|</Styled.Divider>
                                            </Styled.NewInfoItem>
                                        )}


                                        <Styled.NewInfoItem>
                                            <span>{mediaItem?.year}</span>
                                        </Styled.NewInfoItem>

                                        <Styled.NewInfoItem>
                                            <Styled.RedBadge>+13</Styled.RedBadge>
                                        </Styled.NewInfoItem>

                                        <Styled.NewInfoItem>
                                            <span>1h 57min</span>
                                        </Styled.NewInfoItem>
                                    </Styled.NewInfoRow>
                                    <Styled.Wrapper>
                                        <Styled.Title title={mediaItem?.title}>{truncateWords(mediaItem?.title, 100)}</Styled.Title>
                                    </Styled.Wrapper>
                                    <Styled.MediaTextWrapperNewInfoItem>
                                        <Styled.Description title={mediaItem?.description}>{truncateWords(mediaItem?.description, 110)}</Styled.Description>
                                    </Styled.MediaTextWrapperNewInfoItem>
                                    <Styled.WatchNowButton onClick={() => onWatchClick(mediaItem)}>
                                        {
                                            !!mediaItem.planDetails?.length
                                                ? isSignedIn
                                                    ? hasEligiblePlan
                                                        ? LABEL_WATCH_NOW
                                                        : LABEL_PURCHASE_PLAN
                                                    : LABEL_GET_ACCESS
                                                : LABEL_WATCH_NOW
                                        }
                                        <PlayFill color="currentColor" size={20} />
                                    </Styled.WatchNowButton>
                                    {!onlyShowWatchButton && canRate && (
                                        <Styled.MediaRating>
                                            <StarRating
                                                mediaItem={mediaItem}
                                                mediaRating={mediaRating?.rating ?? 0}
                                                sessionToken={sessionToken}
                                                onRatingSuccess={onRatingSuccess}
                                            />
                                        </Styled.MediaRating>
                                    )}

                                </Styled.NewMetaContent>
                            ) : (
                                <Styled.MetaContent>
                                    {!onlyShowWatchButton && (
                                        <>
                                            {showSeparateMetaLayout ? (
                                                // ✅ Separate Layout
                                                <>
                                                    {alignInfoRowStart ? (
                                                        <Styled.InfoRow ref={infoRowRef} alignStart={alignInfoRowStart}>
                                                            {
                                                                mediaItem.duration && (
                                                                    <Styled.InfoItem>
                                                                        <Clock color="currentColor" size={20} />
                                                                        <span>{durationFormatter(mediaItem.duration)}</span>
                                                                    </Styled.InfoItem>
                                                                )
                                                            }

                                                            {shouldShowRating && (
                                                                <Styled.InfoItem>
                                                                    <StarFill color="currentColor" size={20} />
                                                                    <span>{(matchedRating?.rating ?? 0)} / 5</span>
                                                                </Styled.InfoItem>
                                                            )}

                                                            {
                                                                mediaItem.genre && (
                                                                    <Styled.GenrePills>
                                                                        <Styled.Genre key={mediaItem.genre}>{mediaItem.genre}</Styled.Genre>
                                                                    </Styled.GenrePills>
                                                                )
                                                            }
                                                        </Styled.InfoRow>
                                                    ) : (
                                                        <Styled.InfoRowSmallSperated ref={infoRowRef} forceCenter={alignInfoRowSmallCenter}>
                                                            {
                                                                mediaItem.duration && (
                                                                    <Styled.InfoItem>
                                                                        <Clock color="currentColor" size={20} />
                                                                        <span>{durationFormatter(mediaItem.duration)}</span>
                                                                    </Styled.InfoItem>
                                                                )
                                                            }

                                                            {shouldShowRating && (
                                                                <Styled.InfoItem>
                                                                    <StarFill color="currentColor" size={20} />
                                                                    <span>{(matchedRating?.rating ?? 0)} / 5</span>
                                                                </Styled.InfoItem>
                                                            )}

                                                            {
                                                                mediaItem.genre && (
                                                                    <Styled.GenrePills>
                                                                        <Styled.Genre key={mediaItem.genre}>{mediaItem.genre}</Styled.Genre>
                                                                    </Styled.GenrePills>
                                                                )
                                                            }
                                                        </Styled.InfoRowSmallSperated>
                                                    )}

                                                    <Styled.MediaTextWrapper>
                                                        <Styled.MediaTitle>
                                                            {truncateWords(mediaItem?.title, 15)}
                                                        </Styled.MediaTitle>
                                                        <Styled.MediaDescription limitLines={limitDescriptionLines}>
                                                            {truncateWords(mediaItem?.description, 50)}
                                                        </Styled.MediaDescription>
                                                    </Styled.MediaTextWrapper>
                                                </>
                                            ) : (
                                                // ✅ Default Layout
                                                <>
                                                    {alignInfoRowStart ? (
                                                        <Styled.InfoRow ref={infoRowRef} alignStart={alignInfoRowStart}>
                                                            {
                                                                mediaItem.duration && (
                                                                    <Styled.InfoItem>
                                                                        <Clock color="currentColor" size={20} />
                                                                        <span>{durationFormatter(mediaItem.duration)}</span>
                                                                    </Styled.InfoItem>
                                                                )
                                                            }

                                                            {shouldShowRating && (
                                                                <Styled.InfoItem>
                                                                    <StarFill color="currentColor" size={20} />
                                                                    <span>{(matchedRating?.rating ?? 0)} / 5</span>
                                                                </Styled.InfoItem>
                                                            )}

                                                            {
                                                                mediaItem.genre && (
                                                                    <Styled.GenrePills>
                                                                        {mediaItem.genre.split(',').map((genre) => (
                                                                            <Styled.Genre key={genre}>{genre}</Styled.Genre>
                                                                        ))}
                                                                    </Styled.GenrePills>
                                                                )
                                                            }
                                                        </Styled.InfoRow>
                                                    ) : (
                                                        <Styled.InfoRowSmall ref={infoRowRef} forceCenter={alignInfoRowSmallCenter}>
                                                            {
                                                                mediaItem.duration && (
                                                                    <Styled.InfoItem>
                                                                        <Clock color="currentColor" size={20} />
                                                                        <span>{durationFormatter(mediaItem.duration)}</span>
                                                                    </Styled.InfoItem>
                                                                )
                                                            }

                                                            {shouldShowRating && (
                                                                <Styled.InfoItem>
                                                                    <StarFill color="currentColor" size={20} />
                                                                    <span>{(matchedRating?.rating ?? 0)} / 5</span>
                                                                </Styled.InfoItem>
                                                            )}

                                                            {
                                                                mediaItem.genre && (
                                                                    <Styled.GenrePills>
                                                                        {mediaItem.genre.split(',').map((genre) => (
                                                                            <Styled.Genre key={genre}>{genre}</Styled.Genre>
                                                                        ))}
                                                                    </Styled.GenrePills>
                                                                )
                                                            }
                                                        </Styled.InfoRowSmall>
                                                    )}

                                                    <Styled.MediaTextWrapper>
                                                        <Styled.MediaTitle>
                                                            {truncateWords(mediaItem?.title, (!alignInfoRowStart && alignInfoRowSmallCenter) ? 45 : 45)}
                                                        </Styled.MediaTitle>
                                                        <Styled.MediaDescription limitLines={limitDescriptionLines}>
                                                            {truncateWords(mediaItem?.description, 100)}
                                                        </Styled.MediaDescription>
                                                    </Styled.MediaTextWrapper>
                                                </>
                                            )}
                                        </>
                                    )}

                                    {!onlyShowWatchButton && (
                                        <Styled.TrailerButton onClick={() => onWatchClick(mediaItem)}>
                                            {!!mediaItem.planDetails?.length
                                                ? isSignedIn
                                                    ? hasEligiblePlan
                                                        ? LABEL_WATCH_NOW
                                                        : LABEL_PURCHASE_PLAN
                                                    : LABEL_GET_ACCESS
                                                : LABEL_WATCH_NOW}
                                            <PlayFill color="currentColor" size={20} />
                                        </Styled.TrailerButton>
                                    )}

                                    {canRate && (
                                        <Styled.MediaRating>
                                            <StarRating
                                                mediaItem={mediaItem}
                                                mediaRating={mediaRating?.rating ?? 0}
                                                sessionToken={sessionToken}
                                                onRatingSuccess={onRatingSuccess}
                                            />
                                        </Styled.MediaRating>
                                    )}
                                </Styled.MetaContent>
                            )}
                        </Styled.TrailerOverlay>
                    )}
                    <Styled.MediaPosterArt className="media-poster--art">
                        <Styled.BlurredBackground
                            src={mediaItem.thumbnail}
                            alt=""
                            aria-hidden="true"
                        />
                        <Styled.MediaPosterImage
                            src={mediaItem.thumbnail}
                            className={`media-poster--image ${mobileClassCheck()}`}
                            alt="MediaCard Thumbnail"
                        />
                    </Styled.MediaPosterArt>
                </Styled.MediaPoster>

                <Styled.MediaAncillary className={`media-ancillary ${mobileClassCheck()}`}>

                    <Styled.MediaMetadata className="media-metadata">
                        <div style={{ color: 'rgba(255,255,255, 1)' }}>{mediaItem.title}</div>
                    </Styled.MediaMetadata>

                    {(enableRatingControls && isSignedIn && hasEligiblePlan) && (
                        <Styled.MediaRating>
                            <StarRating
                                mediaItem={mediaItem}
                                //mediaRating={mediaRating}
                                mediaRating={mediaRating?.rating ?? 0}
                                sessionToken={sessionToken}
                                onRatingSuccess={onRatingSuccess}
                            />
                        </Styled.MediaRating>
                    )}

                    {mediaItem.description && (
                        <Styled.MediaSynopsis className="media-synopsis">
                            {mediaItem.description}
                        </Styled.MediaSynopsis>
                    )}

                    {mediaItem.genre && (
                        <Styled.MediaGenre className="media-genre">
                            {mediaItem.genre.replace(/,(?=[^\s])/g, ', ')}
                        </Styled.MediaGenre>
                    )}

                    {(mediaItem.directors || mediaItem.producers) && (
                        <Styled.MediaCrew className="media-crew">
                            {mediaItem.directors && (
                                <div><span>Directed by:</span> {mediaItem.directors.replace(/,(?=[^\s])/g, ', ')}</div>
                            )}
                            {mediaItem.producers && (
                                <div><span>Produced by:</span> {mediaItem.producers.replace(/,(?=[^\s])/g, ', ')}</div>
                            )}
                        </Styled.MediaCrew>
                    )}

                </Styled.MediaAncillary>

            </Styled.MediaCardWrapper>
        </Styled.MediaCard>
    );


};
